import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useDataProvider } from 'react-admin';
import { logger } from '@musedrops/shared';
import type { GenModelPreset } from '@musedrops/shared';
import { ModelPresetsList } from '../components/ModelPresetsList';
import { ModelPresetsEditor } from '../components/ModelPresetsEditor';
import { LAYOUT } from '../../../theme';

export const ModelPresetsSettings: React.FC = () => {
  const dataProvider = useDataProvider();
  const [presets, setPresets] = useState<GenModelPreset[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<GenModelPreset | null>(
    null
  );
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load presets on mount
  useEffect(() => {
    loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPresets = async () => {
    try {
      setLoading(true);
      const { data } = await dataProvider.getList('gen_model_presets', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      });
      setPresets(data);
    } catch (error) {
      logger.error('Failed to load presets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = (preset: GenModelPreset | null) => {
    if (hasUnsavedChanges) {
      if (
        !window.confirm(
          'You have unsaved changes. Do you want to discard them?'
        )
      ) {
        return;
      }
    }
    setSelectedPreset(preset);
    setIsCreating(false);
    setHasUnsavedChanges(false);
  };

  const handleCreateNew = () => {
    if (hasUnsavedChanges) {
      if (
        !window.confirm(
          'You have unsaved changes. Do you want to discard them?'
        )
      ) {
        return;
      }
    }
    setSelectedPreset(null);
    setIsCreating(true);
    setHasUnsavedChanges(false);
  };

  const handleSave = async (preset: GenModelPreset) => {
    try {
      if (isCreating) {
        // Create new preset
        const { data } = await dataProvider.create('gen_model_presets', {
          data: preset,
        });
        setSelectedPreset(data);
        setIsCreating(false);
      } else if (selectedPreset) {
        // Update existing preset
        const { data } = await dataProvider.update('gen_model_presets', {
          id: selectedPreset.id,
          data: preset,
        });
        setSelectedPreset(data);
      }
      setHasUnsavedChanges(false);
      await loadPresets();
    } catch (error) {
      logger.error('Failed to save preset:', error);
      throw error;
    }
  };

  const handleDelete = async (presetId: string) => {
    try {
      await dataProvider.delete('gen_model_presets', {
        id: presetId,
      });
      if (selectedPreset?.id === presetId) {
        setSelectedPreset(null);
        setIsCreating(false);
      }
      await loadPresets();
    } catch (error) {
      logger.error('Failed to delete preset:', error);
      throw error;
    }
  };

  const handleDuplicate = async (preset: GenModelPreset) => {
    const newPreset: Partial<GenModelPreset> = {
      ...preset,
      name: `${preset.name} copy`,
      key: `${preset.key}-copy`,
    };
    delete newPreset.id;

    setSelectedPreset(newPreset as GenModelPreset);
    setIsCreating(true);
    setHasUnsavedChanges(true);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 200px)',
        gap: 2,
        py: 3,
        maxWidth: LAYOUT.mediumContentWidth,
        margin: '0 auto',
        width: '100%',
      }}
    >
      {/* Left column - Presets list */}
      <Paper
        sx={{
          width: 300,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ModelPresetsList
          presets={presets}
          selectedPreset={selectedPreset}
          onSelect={handleSelectPreset}
          onCreate={handleCreateNew}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          loading={loading}
        />
      </Paper>

      {/* Right column - Editor */}
      <Paper
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!selectedPreset && !isCreating ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Select a preset
            </Typography>
          </Box>
        ) : (
          <ModelPresetsEditor
            key={selectedPreset?.id || 'new'}
            preset={selectedPreset}
            isCreating={isCreating}
            onSave={handleSave}
            onChange={() => setHasUnsavedChanges(true)}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        )}
      </Paper>
    </Box>
  );
};
